// Inline, multi-tabular editable grid for ONE child (detail) form, rendered
// inside an expanded parent row of EmbedCaptureTabularPreview. It mirrors the
// main grid's look/behaviour (same InlineCellEditor cells, designer column
// widths, Excel paste/fill/range/keyboard) but is a *controlled* editor over
// the parent record's `details[table]` array.
//
// Edits never replace the array from a stale snapshot — every mutation goes
// through `onMutate(prev => next)`, a functional updater the host wires to
// `useEmbedRecords.updateDetailRows`. That keeps a synchronous Excel paste
// loop (many writes in one tick) merging correctly, exactly like the main
// grid's functional setState.
//
// Deletes are *pending*: a removed row keeps its place but is flagged
// `_deleted` and shown struck-through until the main "Save All" commits it
// (existing rows are deleted server-side; never-saved rows just vanish).

import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type FocusEvent } from 'react'
import { ChevronDown, ChevronRight, Columns3, Loader2, Lock, Plus, RotateCcw, Trash2 } from 'lucide-react'

import type { ColumnDef, ItemChangeFormChange, ItemChangeResult, RowCompleteEvent, SqlModel, TransactionModel } from '../types'
import { normalizeItemChange, visionOnLineList } from '../types'
import {
  applyFormsToChildren, childrenForEvent, registerRowCompletionFlusher, rowCompleteWanted,
  rowForEvent, type RowCompletionOutcome,
} from '../screen/detailsBuffer'
import type { DataAccess } from '../../dataBinding'
import {
  buildParentPk, childFormsOf, columnDbName, columnFormKey, gridOrderedColumns,
  inputType, isKey, isMandatory, isProtect, literalDefaults, listHeadingOf,
} from '../utils'
import {
  applyRowEvent, applySeededItemChangeCascades, isInvokableRule, useItemChange,
  type ItemChangeExecutor,
} from './useItemChange'
import { useExcelGrid } from '../../grid/index'
import { InlineCellEditor, coerceCellText, formatForInput } from './InlineCellEditor'
import {
  colKeyOf, layoutIsDirty, orderedDisplayCols, widthOf,
  useColumnDnd, RESIZE_HANDLE_STYLE, type GridLayoutApi,
} from './columnLayout'
import { useTxnRuntimeOptional } from '../context'
import LineEntryBar from '../lineEntry/LineEntryBar'
import { evaluateLineLimits, type ResolvedLineLimit } from '../lineEntry/model'
import { STATUS_TONE, ERROR_TONE, T as LT, kbd as kbdStyle } from '../lineEntry/styles'
import { lineEntryOf } from '../lineEntry/model'
import {
  GridFilterBar, SelectionBand, SetForSelectedDialog, filterCounts, lineAttention, passesFilter,
  useLockedColumns, type GridFilter, type LineAttention,
} from '../lineEntry/gridTools'
import { QuickAddInput, ScanLog, quickAddBinding, useQuickAdd } from '../lineEntry/QuickAdd'
import { useKeyboardShortcuts } from '../keyboard'
import { RunActionBar } from '../FormRenderer'
import type { RunActionResponse } from '../contracts'
import { actionErrorMessages } from '../ActionErrorModal'
import { parseApiError } from '../utils'
import {
  buildVisionActionLines, nextVisionLineNo, visionLineNoColumn, type VisionLineCtx,
} from '../screen/visionLineActions'
import { useUdaScreen } from '../../uda/context'
import { useUdaRows } from '../../uda/useUdaResolve'
import {
  cellApplies, detailsCounts, driverValues, promotedColumns, withUdaColumns,
} from '../../uda/merge'
import { evaluateFlags } from '../../uda/flagEval'
import { FlagIcons } from '../../uda/flags'
import UdaRowDrawer, { UdaDetailsChip } from '../../uda/UdaRowDrawer'
import { UdaMissingLabel, UdaValueCell } from '../../uda/cells'
import { seedUdaProtect, udaProtectOf } from '../../uda/protect'
import { cellMissing } from '../../uda/merge'

export type ChildRow = Record<string, unknown> & {
  _txn_id?: string
  /** Nested grandchild rows, keyed by the grandchild form_no. Entered inline
   *  in an expanded row's sub-grid; committed by useEmbedRecords.saveBatch
   *  after this row is saved (it recurses with this row's composite key). */
  __children?: Record<string, ChildRow[]>
  /** Client-side id for rows not yet saved (stable React key + Excel addr). */
  _localId?: string
  /** Pending-delete marker — committed by Save All, struck-through until then. */
  _deleted?: boolean
  /** Set when an existing (already-saved) row's cell is edited locally. */
  _edited?: boolean
  /** Transient: set after this row saved successfully (green "saved" badge). */
  _saved?: boolean
  /** Set when this row failed to save — record-level message for the row. */
  _error?: string
  /** Column (form_key or db_name) → validation message for failing cells. */
  _fieldErrors?: Record<string, string>
  /** Line Entry: columns the line's source locks (1 = read-only). */
  _protect?: Record<string, 0 | 1>
  /** Line Entry: the bounds the engine resolved when the line was pulled. */
  _limits?: Record<string, ResolvedLineLimit>
}

type Updater = (prev: ChildRow[]) => ChildRow[]

interface Props {
  /** Backend executor for item_change / seeded cascades (the hosting app's
   *  `embedExecute` equivalent) — injected so the package stays HTTP-free. */
  execute: ItemChangeExecutor
  project: string
  objName: string
  formNo: string
  childSm: SqlModel
  rows: ChildRow[]
  /** Functional mutate — wired to useEmbedRecords.updateDetailRows so batched
   *  (paste) edits merge. `coalesceKey` groups same-cell typing into one undo. */
  onMutate: (updater: Updater, coalesceKey?: string) => void
  /** Parent header values, so a child column's {header.X} lookup/item_change
   *  expression resolves against the parent record. */
  parentHeaderValues?: Record<string, unknown>
  uploadDocument: (file: File) => Promise<{ url: string; name?: string }>
  dataAccess?: DataAccess
  busy?: boolean
  /** Parent record lifecycle status, shown in the toolbar so a child edit's
   *  effect on the header record (edited/saved/new) is visible from here. */
  parentStatus?: string
  /** Live column-layout override + persistence, keyed by this child's form_no.
   *  Lets the user drag to resize/reorder the child columns and (as an author)
   *  apply the change back to the design. */
  gridLayout: GridLayoutApi
  // ── Nested (3rd-level+) detail support ──
  /** Full transaction model, so this grid can find its OWN child forms
   *  (grandchildren) and render them as nested sub-grids per row. */
  model?: TransactionModel | null
  /** The owning header record's client id — needed to seed lazy-loaded
   *  grandchildren under a specific saved child row. */
  recordId?: string
  /** Fetch existing rows for a (grand)child form scoped to a parent_pk. */
  loadChildRows?: (formNo: string, parentPk: Record<string, unknown>) =>
    Promise<Array<Record<string, unknown>>>
  /** Seed a grandchild's rows under a saved child row without dirtying. */
  seedRowChildren?: (
    id: string, childFormNo: string, childTxnId: string,
    gcFormNo: string, rows: Array<Record<string, unknown>>,
  ) => void
  /** Nesting depth (1 = a header's direct child). Caps runaway nesting. */
  depth?: number
  /** View-only record: cells render as plain text (the same branch a
   *  pending-delete row uses) and every data-mutating affordance is off —
   *  Add row, delete/restore, the inline editors, and the whole Excel
   *  paste / fill / clear layer. Column drag-resize + reorder stay live
   *  (that's layout, not data), copy/keyboard navigation stays usable, and
   *  the grandchild expanders still open — nested grids inherit the flag. */
  readOnly?: boolean
  /** Caption of the add-row button (the form's `add_label`). */
  addLabel?: string
  /** The parent record's key — lets Line Entry read saved lines it was not
   *  handed. Optional; the buffer is used alone without it. */
  parentPk?: Record<string, unknown>
  /** The saved parent record's txn id (absent while it is new) — Line Entry
   *  gates a pull on an existing record as an edit of it. */
  recordTxnId?: string
  /** Values a row's `row_complete` returned for an ANCESTOR form (the header,
   *  or — for a nested grid — the parent row), keyed by form_no. */
  onAncestorPatch?: (forms: Record<string, ItemChangeFormChange>) => void
  /** Hands the host a way to write ONE cell as if the person typed it (the
   *  item_change cascade fires) — an import review picking a candidate code.
   *  Rows are addressed by `_localId` (or `_txn_id`). Called with null on
   *  unmount. */
  registerCellSetter?: (setter: ChildCellSetter | null) => void
}

/** Writes one cell of a row this grid shows, as a typed edit would. */
export type ChildCellSetter = (rowId: string, field: string, value: unknown) => boolean

const MAX_NEST_DEPTH = 5


const PARENT_STATUS: Record<string, { label: string; bg: string; fg: string }> = {
  retrieved: { label: 'retrieved', bg: 'rgba(100,116,139,0.18)', fg: '#475569' },
  dirty: { label: 'edited', bg: 'rgba(234,179,8,0.20)', fg: '#a16207' },
  unsaved: { label: 'new', bg: 'rgba(59,130,246,0.18)', fg: '#1d4ed8' },
  saved: { label: 'saved', bg: 'rgba(16,185,129,0.20)', fg: '#047857' },
  failed: { label: 'failed', bg: 'rgba(239,68,68,0.20)', fg: '#b91c1c' },
  partial: { label: 'partial', bg: 'rgba(249,115,22,0.20)', fg: '#c2410c' },
}

function uuid(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto
  if (c?.randomUUID) return c.randomUUID()
  return `c_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`
}

const LINENO_W = 34
const SEL_W = 34
const STATUS_W = 58
const ACTIONS_W = 44
// The additional-details chip column ("2/3 · 1 required missing").
const DETAILS_W = 190

function rowKeyOf(row: ChildRow): string {
  return row._txn_id != null ? `t:${row._txn_id}` : `l:${row._localId}`
}

// Per-row change/save status, shown so the user sees what Save All will commit
// and what happened after a save.
function rowStatus(row: ChildRow): { label: string; bg: string; fg: string } | null {
  if (row._error) return { label: 'error', bg: 'rgba(239,68,68,0.20)', fg: '#b91c1c' }
  if (row._deleted) return { label: 'deleted', bg: 'rgba(239,68,68,0.18)', fg: '#b91c1c' }
  if (row._saved) return { label: 'saved', bg: 'rgba(16,185,129,0.20)', fg: '#047857' }
  if (!row._txn_id) return { label: 'new', bg: 'rgba(59,130,246,0.18)', fg: '#1d4ed8' }
  if (row._edited) return { label: 'edited', bg: 'rgba(234,179,8,0.20)', fg: '#a16207' }
  // Existing row loaded from the server, unchanged.
  return { label: 'retrieved', bg: 'rgba(100,116,139,0.18)', fg: '#475569' }
}

// Look up a column's validation error from a row, matching the server's field
// key against either the form_key or the db_name.
function cellError(row: ChildRow, col: ColumnDef): string | undefined {
  const fe = row._fieldErrors
  if (!fe) return undefined
  return fe[columnFormKey(col)] ?? fe[columnDbName(col)]
}

export default function ChildTabularGrid({
  execute, project, objName, formNo, childSm, rows, onMutate,
  parentHeaderValues, uploadDocument, dataAccess, busy, parentStatus, gridLayout,
  model = null, recordId, loadChildRows, seedRowChildren, depth = 1,
  readOnly = false, addLabel, parentPk, recordTxnId, onAncestorPatch, registerCellSetter,
}: Props) {
  const txnRt = useTxnRuntimeOptional()
  // This child form's OWN child forms (grandchildren). When non-empty and we
  // have the wiring to load/commit them, each row gets an expander revealing a
  // nested sub-grid per grandchild form.
  const myChildForms = useMemo(
    () => (model && depth < MAX_NEST_DEPTH ? childFormsOf(model, formNo) : []),
    [model, formNo, depth],
  )
  const canNest = myChildForms.length > 0 && !!loadChildRows && !!seedRowChildren
  const [expandedRowKey, setExpandedRowKey] = useState<string | null>(null)

  // Toggle a row's nested sub-grids. On expanding a SAVED row, lazy-load any
  // grandchild form not yet present (so editing an existing record shows its
  // real grandchildren); a brand-new row starts with empty nested grids that
  // commit after the row itself is saved.
  const toggleExpand = useCallback(async (row: ChildRow) => {
    const key = rowKeyOf(row)
    if (expandedRowKey === key) { setExpandedRowKey(null); return }
    setExpandedRowKey(key)
    if (!canNest || !row._txn_id || !recordId) return
    const ppk = buildParentPk(childSm, row)
    for (const gc of myChildForms) {
      const existing = row.__children?.[gc.formNo]
      if (existing !== undefined) continue
      try {
        const loaded = await loadChildRows!(gc.formNo, ppk)
        seedRowChildren!(recordId, formNo, String(row._txn_id), gc.formNo, loaded)
      } catch {
        seedRowChildren!(recordId, formNo, String(row._txn_id), gc.formNo, [])
      }
    }
  }, [expandedRowKey, canNest, recordId, childSm, myChildForms, loadChildRows, seedRowChildren, formNo])

  // Functional mutate for ONE grandchild form's rows under a specific child
  // row — nests into that row's `__children` via this grid's own onMutate, so
  // batched (paste) edits still merge through the same record update path.
  const mutateGrandchild = useCallback((
    rowKey: string, gcFormNo: string,
  ) => (updater: (prev: ChildRow[]) => ChildRow[], coalesceKey?: string) => {
    onMutate((prev) => prev.map((r) => {
      if (rowKeyOf(r) !== rowKey) return r
      const kids = (r.__children || {}) as Record<string, ChildRow[]>
      return {
        ...r,
        __children: { ...kids, [gcFormNo]: updater(kids[gcFormNo] || []) },
        // Editing a grandchild counts as editing this existing row so the
        // commit pass re-walks its children (it skips truly-untouched rows).
        ...(r._txn_id ? { _edited: true } : {}),
      }
    }), coalesceKey ? `gc:${rowKey}:${gcFormNo}:${coalesceKey}` : `gc:${rowKey}:${gcFormNo}`)
  }, [onMutate])
  // ── Additional attributes (user-defined attributes) ─────────────────
  // Which attributes apply to each line, decided by the server for the page
  // (`uda_resolve` rows scope) and re-asked when a deciding cell changes.
  // Attributes promoted to grids (`in_grid`) become columns (a faint n/a
  // where a line does not have them); the rest live behind the Details chip.
  // Inert without the screen's store, so other hosts see no change.
  const udaApi = useUdaScreen()
  const uda = useUdaRows({
    formNo, rows, rowKey: rowKeyOf, headerValues: parentHeaderValues,
    txnId: recordTxnId, active: !!udaApi,
  })
  const udaOn = uda.resolved
  // Item change sees every attribute; the grid shows the promoted ones.
  const udaSm = useMemo(() => withUdaColumns(childSm, uda.columns), [childSm, uda.columns])
  const udaGridSm = useMemo(
    () => withUdaColumns(childSm, promotedColumns(uda.columns)), [childSm, uda.columns])
  const udaLineFlags = useMemo(
    () => (uda.flags || []).filter(f => (f.show_on || []).includes('line_row')), [uda.flags])
  const [udaDrawerKey, setUdaDrawerKey] = useState<string | null>(null)
  // Each line's attribute lock state ({form_key: 1|0}): seeded from the
  // attributes' own protect rules once the answer brings them, and kept up
  // to date by their live item changes.
  const [udaLocks, setUdaLocks] = useState<Record<string, Record<string, 0 | 1>>>({})
  const udaLocked = (rowKey: string, formKey: string) => udaLocks[rowKey]?.[formKey] === 1

  // Live width/order override for this child form (drag to resize/reorder),
  // applied on top of the designer's column metadata. Order changes are
  // membership-preserving, so the cell lookups below still resolve by key.
  const layout = gridLayout.layouts[formNo]
  const baseCols = useMemo(() => gridOrderedColumns(udaGridSm), [udaGridSm])
  const cols = useMemo(() => orderedDisplayCols(baseCols, layout), [baseCols, layout])
  const wOf = useCallback((c: ColumnDef) => widthOf(c, layout), [layout])
  const layoutDirty = layoutIsDirty(layout)
  const applying = gridLayout.applyingFormNo === formNo
  const dnd = useColumnDnd({
    displayKeys: useMemo(() => cols.map(colKeyOf), [cols]),
    enabled: true,
    onReorder: (keys) => gridLayout.setOrder(formNo, keys),
    onResize: (k, w) => gridLayout.setWidth(formNo, k, w),
  })

  // Latest rows for async callbacks (item_change fires after a debounce).
  const rowsRef = useRef(rows)
  rowsRef.current = rows

  // ── row_complete: a row is FINISHED when focus leaves it ───────────────
  // A row edited since its last completion is "pending"; leaving it (focus to
  // another row of THIS grid, into its nested sub-grid, or out of the grid)
  // runs the form's row_complete. `baseline` is the row before those edits —
  // the `_old` the function compares against; `completed` the row as of its
  // last completion, so finishing it again compares with that.
  const rowCompleteEv = childSm.row_complete
  const rowCompleteOn = !readOnly && isInvokableRule(
    normalizeItemChange(rowCompleteEv as RowCompleteEvent | string | undefined))
  const baselineRef = useRef(new Map<string, Record<string, unknown> | null>())
  const completedRef = useRef(new Map<string, Record<string, unknown>>())
  const focusRowRef = useRef<string | null>(null)
  const leaveTimerRef = useRef<number | null>(null)
  const inflightRef = useRef(new Map<string, Promise<RowCompletionOutcome>>())

  const noteRowEdited = useCallback((rowKey: string) => {
    if (!rowCompleteOn || baselineRef.current.has(rowKey)) return
    const before = rowsRef.current.find(r => rowKeyOf(r) === rowKey)
    const done = completedRef.current.get(rowKey)
    baselineRef.current.set(rowKey, done ?? (before?._txn_id ? rowForEvent(before) : null))
  }, [rowCompleteOn])

  const completeRow = useCallback(async (rowKey: string): Promise<RowCompletionOutcome> => {
    const running = inflightRef.current.get(rowKey)
    if (running) return running
    if (!rowCompleteOn || !baselineRef.current.has(rowKey)) return 'skipped'
    const run = (async (): Promise<RowCompletionOutcome> => {
      const old = baselineRef.current.get(rowKey) ?? null
      baselineRef.current.delete(rowKey)
      const row = rowsRef.current.find(r => rowKeyOf(r) === rowKey)
      if (!row || row._deleted) return 'skipped'
      const settings: Partial<RowCompleteEvent> = (typeof rowCompleteEv === 'object' && rowCompleteEv) ? rowCompleteEv : {}
      if (!rowCompleteWanted(settings.when_changed, row, old)) return 'skipped'
      // A SAVED row's child rows must be known before a rebuild — a replace
      // deletes them; unloaded ones would survive and collide at save.
      let children = (row.__children || {}) as Record<string, ChildRow[]>
      if (row._txn_id && myChildForms.length) {
        const missing = myChildForms.filter(gc => children[gc.formNo] === undefined)
        if (missing.length) {
          if (!loadChildRows) {
            const message = `Cannot complete the row: its ${missing[0].sm.form_title || 'child'} rows are not available here`
            baselineRef.current.set(rowKey, old)
            onMutate(prev => prev.map(r => rowKeyOf(r) === rowKey ? { ...r, _error: message } : r))
            return 'failed'
          }
          const ppk = buildParentPk(childSm, row)
          children = { ...children }
          for (const gc of missing) {
            try {
              children[gc.formNo] = (await loadChildRows(gc.formNo, ppk)) as ChildRow[]
            } catch (err) {
              const message = err instanceof Error ? err.message : String(err)
              baselineRef.current.set(rowKey, old)
              onMutate(prev => prev.map(r => rowKeyOf(r) === rowKey
                ? { ...r, _error: `Could not read its ${gc.sm.form_title || 'child'} rows: ${message}` } : r))
              return 'failed'
            }
          }
        }
      }
      let res: {
        ok?: boolean; error?: string; updates?: Record<string, unknown>
        warning?: string; warnings?: Array<{ message: string }>
        forms?: Record<string, ItemChangeFormChange>
      }
      try {
        res = await execute(dataAccess, project, objName, 'row_complete', {
          form_no: formNo,
          header: rowForEvent(row),
          header_values: parentHeaderValues || {},
          old: old || {},
          children: childrenForEvent(children),
          lines: rowsRef.current.filter(r => rowKeyOf(r) !== rowKey && !r._deleted).map(rowForEvent),
          ...(recordTxnId ? { txn_id: recordTxnId } : {}),
        }) as typeof res
      } catch (err) {
        res = { ok: false, error: err instanceof Error ? err.message : String(err) }
      }
      if (res?.error || res?.ok === false) {
        const message = res?.error || 'The row could not be completed'
        // Pending again: the next leave retries once the user fixes the row.
        baselineRef.current.set(rowKey, old)
        onMutate(prev => prev.map(r => rowKeyOf(r) === rowKey ? { ...r, _error: message } : r))
        txnRt?.notify.error(message)
        return 'failed'
      }
      if (res?.warnings?.length) res.warnings.forEach(w => (txnRt?.notify.warn ?? txnRt?.notify.info)?.(w.message))
      else if (res?.warning) (txnRt?.notify.warn ?? txnRt?.notify.info)?.(res.warning)
      const forms = res?.forms || {}
      const ownSet = { ...(res?.updates || {}), ...(forms[formNo]?.set || {}) }
      const childNos = myChildForms.map(gc => gc.formNo)
      onMutate(prev => prev.map(r => {
        if (rowKeyOf(r) !== rowKey) return r
        // Children loaded just now fill only the forms the row did not have.
        const base = { ...r, __children: { ...children, ...(r.__children || {}) } }
        const kids = applyFormsToChildren(
          base.__children as Record<string, ChildRow[]>, forms, childNos,
          (x) => ({ ...x, _localId: uuid() }))
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { _error, ...rest } = base
        const touched = kids.changed || Object.keys(ownSet).length > 0
        return {
          ...rest, ...ownSet,
          __children: kids.children as Record<string, ChildRow[]>,
          ...(r._txn_id && touched ? { _edited: true } : {}),
        }
      }), `rc:${rowKey}`)
      const up: Record<string, ItemChangeFormChange> = {}
      for (const [fno, change] of Object.entries(forms)) {
        if (fno === formNo || childNos.includes(fno)) continue
        if (change?.set && Object.keys(change.set).length) up[fno] = change
      }
      if (Object.keys(up).length) onAncestorPatch?.(up)
      completedRef.current.set(rowKey, rowForEvent({ ...row, ...ownSet }))
      return 'ran'
    })()
    inflightRef.current.set(rowKey, run)
    try {
      return await run
    } finally {
      inflightRef.current.delete(rowKey)
    }
  }, [rowCompleteOn, rowCompleteEv, myChildForms, loadChildRows, childSm, execute, dataAccess,
      project, objName, formNo, parentHeaderValues, recordTxnId, onMutate, onAncestorPatch, txnRt])

  // Which of THIS grid's rows a focus target sits in (null: a nested grid's
  // cell, the toolbar, or outside).
  const rowOfTarget = useCallback((t: EventTarget | null): string | null => {
    const el = t as HTMLElement | null
    const cell = el?.closest?.('[data-grid-row]') as HTMLElement | null
    if (!cell || cell.closest('[data-grid-scope]') !== containerRef.current) return null
    return cell.getAttribute('data-grid-row')
  }, [])

  const onGridFocus = useCallback((e: FocusEvent) => {
    if (leaveTimerRef.current != null) { window.clearTimeout(leaveTimerRef.current); leaveTimerRef.current = null }
    const key = rowOfTarget(e.target)
    const prev = focusRowRef.current
    if (prev && prev !== key) void completeRow(prev)
    focusRowRef.current = key
  }, [rowOfTarget, completeRow])

  const onGridBlur = useCallback((e: FocusEvent) => {
    const to = e.relatedTarget as Node | null
    if (to && containerRef.current?.contains(to)) return  // onGridFocus handles it
    const prev = focusRowRef.current
    if (!prev) return
    // Left the grid (Save, a toolbar button, another field…). A lookup
    // popup that hands focus straight back is not a leave — wait a beat.
    if (leaveTimerRef.current != null) window.clearTimeout(leaveTimerRef.current)
    leaveTimerRef.current = window.setTimeout(() => {
      leaveTimerRef.current = null
      const active = typeof document !== 'undefined' ? document.activeElement : null
      if (active && rowOfTarget(active) === prev) return
      focusRowRef.current = null
      void completeRow(prev)
    }, 250)
  }, [rowOfTarget, completeRow])

  // A nested grid's row event addressed THIS grid's row (or above): apply this
  // form's values to that row and pass the rest up.
  const patchFromNested = useCallback((rowKey: string) => (forms: Record<string, ItemChangeFormChange>) => {
    const mine = forms[formNo]?.set
    if (mine && Object.keys(mine).length) {
      onMutate(prev => prev.map(r => rowKeyOf(r) === rowKey
        ? { ...r, ...mine, ...(r._txn_id ? { _edited: true } : {}) } : r))
    }
    const rest: Record<string, ItemChangeFormChange> = {}
    for (const [fno, change] of Object.entries(forms)) if (fno !== formNo) rest[fno] = change
    if (Object.keys(rest).length) onAncestorPatch?.(rest)
  }, [formNo, onMutate, onAncestorPatch])

  // Save asks every grid to finish its pending rows first.
  useEffect(() => {
    if (!rowCompleteOn) return
    return registerRowCompletionFlusher(async () => {
      if (leaveTimerRef.current != null) { window.clearTimeout(leaveTimerRef.current); leaveTimerRef.current = null }
      const keys = Array.from(new Set([...baselineRef.current.keys(), ...inflightRef.current.keys()]))
      if (!keys.length) return 'skipped'
      const outcomes = await Promise.all(keys.map(k => completeRow(k)))
      return outcomes.includes('failed') ? 'failed' : outcomes.includes('ran') ? 'ran' : 'skipped'
    })
  }, [rowCompleteOn, completeRow])

  const triggerItemChange = useItemChange<{ rowKey: string }>({
    execute, dataAccess,
    project, objName, sm: udaSm,
    onUpdates: (ctx, updates, result) => {
      if (Object.keys(updates).length) {
        onMutate(prev => prev.map(r =>
          rowKeyOf(r) === ctx.rowKey ? { ...r, ...updates } : r,
        ), `ic:${ctx.rowKey}`)
      }
      // An attribute rule's lock / unlock (a line's own columns keep no
      // per-row lock state, so only attribute keys are taken).
      const locks = udaProtectOf(result?.protect as Record<string, unknown> | undefined)
      if (Object.keys(locks).length) {
        setUdaLocks(prev => ({ ...prev, [ctx.rowKey]: { ...(prev[ctx.rowKey] || {}), ...locks } }))
      }
    },
  })

  // Seed the lock state of each line's attributes once per (line, applicable
  // attributes, layer version): their protect rules fired over the stored
  // values. A line that left the grid by the time an answer lands is skipped.
  const udaSeededRef = useRef(new Set<string>())
  useEffect(() => {
    if (!uda.resolved || uda.columns.length === 0) return
    const todo: Array<{ key: string; attrKeys: string[] }> = []
    for (const r of rowsRef.current) {
      if (r._deleted) continue
      const key = rowKeyOf(r)
      const res = uda.rowOf(key)
      if (!res || !res.attr_keys.length) continue
      const mark = `${key}|${uda.defVersion}|${res.attr_keys.join(',')}`
      if (udaSeededRef.current.has(mark)) continue
      udaSeededRef.current.add(mark)
      todo.push({ key, attrKeys: res.attr_keys })
    }
    if (!todo.length) return
    let cancelled = false
    void (async () => {
      for (const t of todo) {
        if (cancelled) return
        const row = rowsRef.current.find(r => rowKeyOf(r) === t.key)
        if (!row) continue
        try {
          const locks = await seedUdaProtect(uda.columns, row, async (col, hdr) => (await execute(
            dataAccess, project, objName, 'item_change', {
              form_no: formNo, field_name: columnFormKey(col), header: hdr,
              header_values: parentHeaderValues || {},
            })) as ItemChangeResult, t.attrKeys)
          if (cancelled || !Object.keys(locks).length) continue
          setUdaLocks(prev => ({ ...prev, [t.key]: { ...(prev[t.key] || {}), ...locks } }))
        } catch { /* best-effort: the line stays editable */ }
      }
    })()
    return () => { cancelled = true }
    // parentHeaderValues is read as of the answer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uda.rows, uda.resolved, uda.columns, uda.defVersion, execute, dataAccess, project, objName, formNo])

  // Apply one cell edit + fire the column's item_change cascade. The header
  // for item_change is built synchronously from the latest rows (the onMutate
  // updater runs later during commit), mirroring the main grid's onCellChange.
  const onCellChange = useCallback((rowKey: string, dbName: string, v: unknown) => {
    noteRowEdited(rowKey)
    // Keep rowsRef current WITHIN the tick too: an Excel paste writes every
    // cell of a row before React re-renders, and the next cell's write (and
    // each row's debounced cascade) must see the values already pasted.
    rowsRef.current = rowsRef.current.map(r => (
      rowKeyOf(r) === rowKey ? { ...r, [dbName]: v } : r
    ))
    onMutate(prev => prev.map(r => {
      if (rowKeyOf(r) !== rowKey) return r
      // Editing clears the prior save outcome (saved/error/field errors) and,
      // for an existing row, flags it as edited; new rows already read "new".
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { _saved, _error, _fieldErrors, ...rest } = r
      return { ...rest, [dbName]: v, ...(r._txn_id ? { _edited: true } : {}) }
    }), `${rowKey}:${dbName}`)
    // The row is read when the debounced call goes out, so it carries every
    // cell the paste (or quick typing) set on it — not the state at this cell.
    triggerItemChange(
      { rowKey }, dbName,
      () => ({ ...(rowsRef.current.find(r => rowKeyOf(r) === rowKey) || {}) }),
      parentHeaderValues, formNo,
    )
  }, [onMutate, triggerItemChange, parentHeaderValues, formNo, noteRowEdited])

  // Register ONE stable setter for the host; it reads the latest rows and
  // onCellChange through refs, so it never needs re-registering.
  const onCellChangeRef = useRef(onCellChange)
  onCellChangeRef.current = onCellChange
  const registerRef = useRef(registerCellSetter)
  registerRef.current = registerCellSetter
  useEffect(() => {
    const register = registerRef.current
    if (!register) return
    register((rowId, field, value) => {
      const row = rowsRef.current.find(r => (
        String(r._localId ?? '') === rowId || (r._txn_id != null && String(r._txn_id) === rowId)
      ))
      if (!row) return false
      onCellChangeRef.current(rowKeyOf(row), field, value)
      return true
    })
    return () => register(null)
  }, [])

  // The values a NEW line opens with: the column defaults, then the cascades
  // those defaults trigger, then the form's row_add event — the same steps a
  // popup "Add row" takes. Best-effort: each step returns its input on failure.
  const seedNewRow = useCallback(async (): Promise<ChildRow> => {
    let values: Record<string, unknown> = literalDefaults(childSm)
    values = await applySeededItemChangeCascades(
      execute, dataAccess, project, objName, childSm, values, parentHeaderValues, formNo,
    )
    values = await applyRowEvent(
      execute, dataAccess, project, objName, childSm, 'row_add', values,
      parentHeaderValues, formNo,
    )
    return values
  }, [execute, dataAccess, project, objName, childSm, parentHeaderValues, formNo])

  // ── Paste summary (canvas board 5) ──────────────────────────────────
  // A burst of cell writes across several rows in one go is a paste: keep the
  // rows as they were before it (for "Undo paste") and which rows / columns
  // it touched, then summarise once the burst settles.
  const burstRef = useRef<{ snapshot: ChildRow[]; rows: Set<string>; cols: Set<string>; timer: number | null } | null>(null)
  const [paste, setPaste] = useState<{ snapshot: ChildRow[]; rows: string[]; cols: string[] } | null>(null)
  const notePasteWrite = useCallback((rowKey: string, dbName: string) => {
    let b = burstRef.current
    if (!b) {
      b = { snapshot: rowsRef.current, rows: new Set(), cols: new Set(), timer: null }
      burstRef.current = b
    }
    b.rows.add(rowKey)
    b.cols.add(dbName)
    if (b.timer != null) window.clearTimeout(b.timer)
    b.timer = window.setTimeout(() => {
      const done = burstRef.current
      burstRef.current = null
      if (done && done.rows.size > 1) {
        setPaste({ snapshot: done.snapshot, rows: Array.from(done.rows), cols: Array.from(done.cols) })
      }
    }, 60)
  }, [])

  // ── Excel layer (paste / fill / range / keyboard) ──────────────────
  const writeExcelCell = useCallback((rowKey: string, dbName: string, text: string) => {
    // Every excelGrid mutation (paste, Delete-clear, fill down/right) funnels
    // through writeCell, so this one guard turns the whole layer read-only.
    if (readOnly) return
    const col = cols.find(c => columnFormKey(c) === dbName)
    if (!col) return
    const rec = rowsRef.current.find(r => rowKeyOf(r) === rowKey)
    if (!rec || rec._deleted) return
    // Same skip rule as the main grid: protected cols + locked keys on saved
    // rows — plus the columns a pulled line's source locks, and an additional
    // attribute the line does not have (n/a).
    if (isProtect(col) || (!!rec._txn_id && isKey(col)) || rec._protect?.[dbName] === 1) return
    if (!cellApplies(col, uda.rowOf(rowKey)?.attr_keys) || udaLocks[rowKey]?.[dbName] === 1) return
    notePasteWrite(rowKey, dbName)
    onCellChange(rowKey, dbName, coerceCellText(col, text))
  }, [cols, onCellChange, readOnly, notePasteWrite, uda, udaLocks])

  const readExcelCell = useCallback((rowKey: string, dbName: string) => {
    const col = cols.find(c => columnFormKey(c) === dbName)
    const rec = rowsRef.current.find(r => rowKeyOf(r) === rowKey)
    if (!col || !rec) return null
    return formatForInput(rec[dbName], inputType(col))
  }, [cols])

  const ensureExcelRows = useCallback(async (count: number) => {
    if (readOnly) return
    if (count <= rowsRef.current.length) return
    // Rows a paste grows start like any new line (defaults + row_add) rather
    // than blank. The seed depends only on the form and its parent, so it is
    // computed ONCE for the whole paste, then the pasted cells land on top
    // and fire their own per-row cascades.
    const seed = await seedNewRow()
    const have = rowsRef.current.length
    if (count <= have) return
    const grown: ChildRow[] = []
    for (let i = have; i < count; i += 1) grown.push({ ...seed, _localId: uuid() })
    // Built outside the updater (React may run it late, or twice in strict
    // mode); the updater only tops the CURRENT rows up to `count`.
    onMutate(prev => (
      count <= prev.length ? prev : [...prev, ...grown.slice(0, count - prev.length)]
    ))
    // The paste writes into these rows before React re-renders.
    rowsRef.current = [...rowsRef.current, ...grown]
  }, [onMutate, readOnly, seedNewRow])

  const attachGrid = useExcelGrid({
    basicNav: true,
    // Hide the drag-to-fill handle on a view-only record. Copy, range select
    // and keyboard navigation stay on — the user can still read and copy out.
    fillHandle: !readOnly,
    writeCell: writeExcelCell,
    readCell: readExcelCell,
    ensureRowCount: ensureExcelRows,
  })

  // Keep our own handle on the grid container (the Excel layer uses a callback
  // ref) so clicking an error badge can scroll to + focus the failing cell.
  const containerRef = useRef<HTMLDivElement | null>(null)
  // Visible width of THIS grid's horizontal scroll viewport. A nested
  // (grandchild) expansion panel pins itself to this width (position:sticky)
  // so it scrolls inside its own bounds and its Add/Refresh toolbar stays
  // on-screen — instead of inheriting this grid's full (wide) table width.
  const [viewportWidth, setViewportWidth] = useState(0)
  const viewportRoRef = useRef<ResizeObserver | null>(null)
  const setGridRef = useCallback((node: HTMLDivElement | null) => {
    containerRef.current = node
    attachGrid(node)
    viewportRoRef.current?.disconnect()
    viewportRoRef.current = null
    if (!node) return
    const update = () => setViewportWidth(node.clientWidth)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(node)
    viewportRoRef.current = ro
  }, [attachGrid])

  // The form_key of a failed row's first errored column (mapping the server's
  // field — form_key or db_name — back to the column), for click-to-navigate.
  const firstErrorCol = useCallback((row: ChildRow): string | null => {
    const fe = row._fieldErrors
    const field = fe ? Object.keys(fe)[0] : undefined
    if (field) {
      const col = cols.find(c => columnFormKey(c) === field || columnDbName(c) === field)
      if (col) return columnFormKey(col)
    }
    return cols[0] ? columnFormKey(cols[0]) : null
  }, [cols])

  const navigateToCell = useCallback((rowKey: string, col: string | null) => {
    const root = containerRef.current
    if (!root || !col) return
    const cell = root.querySelector<HTMLElement>(
      `[data-grid-row="${rowKey}"][data-grid-col="${col}"]`,
    )
    if (!cell) return
    cell.scrollIntoView({ block: 'nearest', inline: 'nearest' })
    cell.querySelector<HTMLElement>('input, select, textarea, button')?.focus()
  }, [])

  // ── Row actions ────────────────────────────────────────────────────
  const addRow = useCallback(async () => {
    if (readOnly) return
    const seeded = await seedNewRow()
    onMutate(prev => [...prev, { ...seeded, _localId: uuid() }])
  }, [seedNewRow, onMutate, readOnly])

  const toggleDelete = useCallback((rowKey: string) => {
    if (readOnly) return
    onMutate(prev => prev.map(r => {
      if (rowKeyOf(r) !== rowKey) return r
      // A never-saved row that's marked deleted is simply dropped.
      if (!r._txn_id && !r._deleted) return { ...r, _deleted: true }
      return { ...r, _deleted: !r._deleted }
    }))
  }, [onMutate, readOnly])

  // ── Line Entry tools: exceptions, selection, set-for-selected, scan ──
  // (canvas boards 3, 4 and 6). Shown when the form declares Line Entry or a
  // line carries limits / an error.
  const le = useMemo(() => lineEntryOf(childSm), [childSm])
  const labelOf = useCallback((k: string) => {
    const c = cols.find(x => columnFormKey(x) === k)
    return c ? String(c.heading || c.name) : k
  }, [cols])
  const attn = useMemo(() => {
    const m = new Map<string, LineAttention>()
    for (const r of rows) if (!r._deleted) m.set(rowKeyOf(r), lineAttention(r, labelOf))
    return m
  }, [rows, labelOf])
  const showTools = !!le || rows.some(r => !!r._limits || !!r._error)
  const [filter, setFilter] = useState<GridFilter>('all')
  const counts = useMemo(() => filterCounts(Array.from(attn.values())), [attn])
  const shownRows = useMemo(() => (filter === 'all' ? rows : rows.filter(r => {
    const a = attn.get(rowKeyOf(r))
    return a ? passesFilter(filter, a) : false
  })), [rows, filter, attn])
  const lineNoOf = useMemo(() => {
    const m = new Map<string, number>()
    let n = 0
    for (const r of rows) if (!r._deleted) m.set(rowKeyOf(r), ++n)
    return m
  }, [rows])
  const [selectedRaw, setSelected] = useState<Set<string>>(() => new Set())
  const selected = useMemo(() => {
    const live = new Set(rows.filter(r => !r._deleted).map(rowKeyOf))
    return new Set(Array.from(selectedRaw).filter(k => live.has(k)))
  }, [selectedRaw, rows])
  const toggleSel = (k: string) => setSelected(prev => {
    const next = new Set(prev)
    if (next.has(k)) next.delete(k)
    else next.add(k)
    return next
  })
  // ── Vision line buttons (OBJ_ACTIONS.PAGE_CONTEXT 1: Stock, Default,
  // Delete All…) — the same toolbar the line form shows (ChildFormTab), built
  // from the same helpers (visionLineActions). They run on the CURRENT line:
  // the one last clicked or typed in (Vision's current detail record); with
  // none, on a new blank line.
  const [currentLineKey, setCurrentLineKey] = useState<string | null>(null)
  const lineListActions = useMemo(
    () => (childSm.actions || []).filter(a => visionOnLineList(a)), [childSm])
  const lineNoCol = useMemo(() => visionLineNoColumn(childSm, parentPk, true), [childSm, parentPk])
  const currentRow = currentLineKey
    ? rows.find(r => rowKeyOf(r) === currentLineKey && !r._deleted) : undefined
  const liveLineCount = rows.filter(r => !r._deleted).length
  const gridLine = useMemo(() => {
    if (currentRow) {
      const values: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(currentRow)) if (!k.startsWith('_')) values[k] = v
      return { values, isNew: !currentRow._txn_id }
    }
    const blank = literalDefaults(childSm)
    if (lineNoCol) blank[columnFormKey(lineNoCol)] = nextVisionLineNo(lineNoCol, rows)
    return { values: blank, isNew: true }
  }, [currentRow, childSm, lineNoCol, rows])
  const visionLineCtx: VisionLineCtx = {
    sm: childSm, formNo, parentPk, parentHeaderValues,
    exec: (action, payload) => execute(dataAccess, project, objName, action, payload),
    onItemChangeError: (m) => txnRt?.notify.error(m),
  }
  // The rows a line button returned become lines (MasterStatefulEJB.
  // setActionData): a NEW line the button was pressed on (typed in, not saved)
  // takes the first row and the rest go beside it; on a saved line, or none,
  // every row is a new line.
  const addActionLines = async (resRows: Array<Record<string, unknown>>) => {
    const replace = currentRow && !currentRow._txn_id ? rowKeyOf(currentRow) : null
    const existing = rowsRef.current.filter(r => rowKeyOf(r) !== replace)
    const lines = await buildVisionActionLines(
      visionLineCtx, resRows, gridLine.isNew ? gridLine.values : null, lineNoCol, existing)
    if (!lines.length) return
    const added: ChildRow[] = lines.map(l => ({ ...l, _localId: uuid() }))
    onMutate(prev => {
      const i = replace ? prev.findIndex(r => rowKeyOf(r) === replace) : -1
      return i < 0 ? [...prev, ...added] : [...prev.slice(0, i), ...added, ...prev.slice(i + 1)]
    })
    setCurrentLineKey(null)
    txnRt?.notify.success(lines.length === 1 ? '1 line added' : `${lines.length} lines added`)
  }
  const applyLineActionResult = (res: RunActionResponse) => {
    if (res.handler === 'select_grid') {
      const resRows = res.rows || []
      if (resRows.length && (!res.target_form_no || String(res.target_form_no) === formNo)) {
        void addActionLines(resRows)
      }
      return
    }
    // An item-change style result updates the current line.
    const set = { ...(res.updates || {}), ...(res.forms?.[formNo]?.set || {}) }
    if (currentRow && Object.keys(set).length) {
      const k = rowKeyOf(currentRow)
      onMutate(prev => prev.map(r => rowKeyOf(r) === k
        ? { ...r, ...set, ...(r._txn_id ? { _edited: true } : {}) } : r))
    }
  }
  // Vision's "Delete All" (SERVICE_HANDLER 'D'): every line goes, the way one
  // line's delete does in Vision — a saved line is flagged deleted (removed by
  // Save, restorable until then), a line not saved yet is dropped.
  const deleteAllLines = () => {
    const n = rowsRef.current.filter(r => !r._deleted).length
    onMutate(prev => prev.flatMap(r => (r._deleted ? [r] : r._txn_id ? [{ ...r, _deleted: true }] : [])))
    setCurrentLineKey(null)
    setSelected(new Set())
    if (n) txnRt?.notify.success(n === 1 ? '1 line deleted' : `${n} lines deleted`)
  }
  const lineButtons = !readOnly && txnRt && lineListActions.length > 0 ? (
    <span
      title={currentRow ? 'Runs on the current line' : 'Runs on a new line — click a line to run on it'}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
    >
      <RunActionBar
        actions={lineListActions}
        include={(a) => visionOnLineList(a)}
        inline
        onVisionDeleteAll={deleteAllLines}
        surface={recordTxnId ? 'edit' : 'add'}
        project={project}
        objName={objName}
        formNo={formNo}
        values={gridLine.values}
        headerValues={parentHeaderValues}
        txnId={currentRow?._txn_id ? String(currentRow._txn_id) : undefined}
        parentPk={parentPk}
        lineContext={{ rowIsNew: gridLine.isNew, lineCount: liveLineCount + (currentRow ? 0 : 1) }}
        onActionResult={applyLineActionResult}
        onActionError={(err) => txnRt.notify.error(actionErrorMessages(parseApiError(err)).join('\n'))}
      />
    </span>
  ) : null

  const allShownSel = shownRows.some(r => !r._deleted)
    && shownRows.every(r => r._deleted || selected.has(rowKeyOf(r)))
  const lockedCols = useLockedColumns(le?.sources, cols)
  const [setFor, setSetFor] = useState<ColumnDef | null>(null)
  const [colMenu, setColMenu] = useState<string | null>(null)
  // Columns summed in the footer: each source's picker totals, else its
  // status column.
  const totalCols = useMemo(() => {
    const names = new Set<string>()
    for (const src of le?.sources || []) {
      const t = src.picker?.totals
      if (t && t.length) t.forEach(x => names.add(String(x).toLowerCase()))
      else for (const [col, l] of Object.entries(src.limits || {})) if (l?.status) names.add(col.toLowerCase())
    }
    return cols.filter(c => names.has(String(c.name).toLowerCase()) || names.has(columnFormKey(c)))
  }, [le, cols])
  const statusSrc = le?.sources.find(s => Object.values(s.limits || {}).some(l => l?.status))
  const statusColKey = useMemo(() => {
    const name = statusSrc ? Object.entries(statusSrc.limits || {}).find(([, l]) => l?.status)?.[0] : undefined
    const c = name ? cols.find(x => String(x.name).toLowerCase() === name.toLowerCase()) : undefined
    return c ? columnFormKey(c) : null
  }, [statusSrc, cols])

  const setForRows = (col: ColumnDef, value: unknown, onlyBlank: boolean) => {
    const k = columnFormKey(col)
    for (const r of rowsRef.current) {
      const rk = rowKeyOf(r)
      if (!selected.has(rk) || r._deleted || r._protect?.[k] === 1) continue
      if (onlyBlank && !(r[k] == null || r[k] === '')) continue
      onCellChange(rk, k, value)
    }
  }
  const fillDownSelected = (col: ColumnDef) => {
    const k = columnFormKey(col)
    const ordered = rowsRef.current.filter(r => selected.has(rowKeyOf(r)) && !r._deleted)
    if (ordered.length < 2) return
    const v = ordered[0][k]
    for (const r of ordered.slice(1)) if (r._protect?.[k] !== 1) onCellChange(rowKeyOf(r), k, v)
  }
  const setToLimit = () => {
    if (!statusColKey) return
    for (const r of rowsRef.current) {
      const rk = rowKeyOf(r)
      const max = r._limits?.[statusColKey]?.max
      if (selected.has(rk) && !r._deleted && max != null) onCellChange(rk, statusColKey, max)
    }
  }

  const qaCfg = le?.quickAdd || null
  const qa = useQuickAdd({
    execute: (action, payload) => execute(dataAccess, project, objName, action, payload),
    formNo,
    headerValues: parentHeaderValues || {},
    txnId: recordTxnId,
    parentPk,
    rows,
    rowKeyOf: (r) => rowKeyOf(r as ChildRow),
    onAppend: (added) => {
      const withIds = added.map(r => ({ ...r, _localId: uuid() })) as ChildRow[]
      onMutate(prev => [...prev, ...withIds])
      rowsRef.current = [...rowsRef.current, ...withIds]
      return withIds.map(rowKeyOf)
    },
    onSetCell: onCellChange,
    onRemoveRow: (rk) => onMutate(prev => prev.flatMap(r => {
      if (rowKeyOf(r) !== rk) return [r]
      return r._txn_id ? [{ ...r, _deleted: true }] : []
    })),
    labelOf,
  })
  const qaRef = useRef<HTMLInputElement | null>(null)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const [logHidden, setLogHidden] = useState(false)
  // What a scan would hit among the lines already here (the drop-down).
  const qaBind = useMemo(() => quickAddBinding(le, cols), [le, cols])
  const qaKeys = qaBind.keys
  const qaQty = qaBind.qty
  const qaSuggest = useCallback((code: string) => {
    const want = code.trim().toLowerCase()
    if (!want || !qaKeys.length) return []
    const out: Array<{ key: string; code: string; name: string; preview: string }> = []
    for (const r of rowsRef.current) {
      if (r._deleted) continue
      const key = qaKeys.find(k => String(r[k] ?? '').toLowerCase().startsWith(want))
      if (!key) continue
      const ci = cols.findIndex(c => columnFormKey(c) === key)
      const nameCol = cols[ci + 1]
      const q = qaQty ? Number(r[qaQty] || 0) : null
      out.push({
        key: rowKeyOf(r), code: String(r[key]),
        name: nameCol ? String(r[columnFormKey(nameCol)] ?? '') : '',
        preview: `line ${lineNoOf.get(rowKeyOf(r)) ?? '?'}${q != null ? ` · ${q.toLocaleString()} → …` : ''}`,
      })
    }
    return out
  }, [qaKeys, qaQty, cols, lineNoOf])
  const nextIssue = () => {
    const keys = rows.filter(r => !r._deleted && attn.get(rowKeyOf(r))?.needs).map(rowKeyOf)
    if (!keys.length) return
    const active = document.activeElement?.closest?.('[data-grid-row]')?.getAttribute('data-grid-row')
    const i = active ? keys.indexOf(active) : -1
    const target = keys[(i + 1) % keys.length]
    const row = rows.find(r => rowKeyOf(r) === target)
    if (row) navigateToCell(target, firstErrorCol(row))
  }
  useKeyboardShortcuts([
    { combo: 'f6', run: () => qaRef.current?.focus() },
    { combo: 'alt+arrowdown', run: nextIssue },
  ], { enabled: showTools && !readOnly, scopeRef: rootRef })

  const tableWidth = useMemo(
    () => cols.reduce((sum, c) => sum + wOf(c),
      LINENO_W + STATUS_W + ACTIONS_W + (showTools ? SEL_W : 0) + (udaOn ? DETAILS_W : 0)),
    [cols, wOf, showTools, udaOn],
  )
  const liveCount = rows.filter(r => !r._deleted).length

  let visibleLine = 0

  return (
    <div ref={rootRef} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        padding: '5px 8px', borderBottom: '1px solid var(--color-border)',
      }}>
        {qaCfg && !readOnly && (
          <QuickAddInput ref={qaRef} qa={qa} suggestions={qaSuggest} width={330} />
        )}
        {showTools && liveCount > 0 && (
          <GridFilterBar value={filter} counts={counts} onChange={setFilter} />
        )}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
          <span>
            {rows.filter(r => !r._deleted).length} row
            {rows.filter(r => !r._deleted).length === 1 ? '' : 's'}
          </span>
          {parentStatus && PARENT_STATUS[parentStatus] && (
            <span
              title="Status of the parent (header) record"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '1px 7px', borderRadius: 8,
                fontSize: '0.8125rem', fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '0.03em',
                background: PARENT_STATUS[parentStatus].bg,
                color: PARENT_STATUS[parentStatus].fg,
              }}
            >Header: {PARENT_STATUS[parentStatus].label}</span>
          )}
          {uda.error && (
            <span role="status" title={uda.error} style={{ color: 'var(--color-warning)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              Additional details could not be loaded
              <button type="button" onClick={uda.retry} style={{ border: 0, background: 'transparent', padding: 0,
                color: 'var(--color-accent)', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>Try again</button>
            </span>
          )}
        </div>
        {gridLayout.canEdit && layoutDirty && (
          <>
            <button
              type="button"
              onClick={() => gridLayout.reset(formNo)}
              disabled={applying}
              className="btn btn-ghost btn-xs"
              title="Discard the column width / order changes made here"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '3px 8px', fontSize: '0.8125rem',
                border: '1px solid var(--color-border)', borderRadius: 4,
                background: 'var(--color-bg)', color: 'var(--color-text-secondary)',
                cursor: applying ? 'not-allowed' : 'pointer',
              }}
            ><RotateCcw size={12} /> Reset</button>
            <button
              type="button"
              onClick={() => gridLayout.apply(formNo)}
              disabled={applying}
              className="btn btn-ghost btn-xs"
              title="Save the new column widths & order into the transaction design"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '3px 8px', fontSize: '0.8125rem',
                border: '1px solid var(--color-border)', borderRadius: 4,
                background: 'var(--color-bg)', color: 'var(--color-accent)',
                cursor: applying ? 'not-allowed' : 'pointer',
              }}
            >
              {applying
                ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                : <Columns3 size={12} />} Apply layout
            </button>
          </>
        )}
        {showTools && !readOnly && (
          <button type="button" disabled={selected.size === 0}
            title={selected.size ? undefined : 'Tick lines first'}
            onClick={() => setColMenu(colMenu === '__pick' ? null : '__pick')}
            className="btn btn-ghost btn-xs"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', fontSize: '0.8125rem',
              border: '1px solid var(--color-border)', borderRadius: 4, background: 'var(--color-bg)',
              color: 'var(--color-text)', cursor: selected.size ? 'pointer' : 'not-allowed',
              opacity: selected.size ? 1 : 0.6, position: 'relative',
            }}
          >Set for selected…
            {colMenu === '__pick' && (
              <span role="menu" style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 20,
                background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 8,
                boxShadow: '0 12px 32px rgba(20,28,40,.18)', padding: '4px 0', minWidth: 200, textAlign: 'left' }}>
                {cols.filter(c => !isProtect(c) && !isKey(c)).map(c => (
                  <span key={columnFormKey(c)} role="menuitem" tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); setColMenu(null); setSetFor(c) }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setColMenu(null); setSetFor(c) } }}
                    style={{ display: 'block', padding: '7px 12px', fontSize: 13, cursor: 'pointer' }}>
                    {c.heading || c.name}
                  </span>
                ))}
              </span>
            )}
          </button>
        )}
        <LineEntryBar
          sm={childSm}
          formNo={formNo}
          execute={(action, payload) => execute(dataAccess, project, objName, action, payload)}
          engineKey={project}
          headerValues={parentHeaderValues || {}}
          parentPk={parentPk}
          txnId={recordTxnId}
          rows={rows}
          onAppend={(added) => onMutate(prev => [...prev, ...added.map(r => ({ ...r, _localId: uuid() }))])}
          onRemove={(idx) => {
            const drop = new Set(idx.map(i => rowKeyOf(rows[i])))
            onMutate(prev => prev.flatMap(r => {
              if (!drop.has(rowKeyOf(r))) return [r]
              return r._txn_id ? [{ ...r, _deleted: true }] : []
            }))
          }}
          notify={txnRt?.notify || { success: () => {}, error: (m) => console.warn(m) }}
          readOnly={readOnly}
        />
        {lineButtons}
        {!readOnly && (
          <button
            type="button"
            onClick={addRow}
            disabled={busy}
            className="btn btn-ghost btn-xs"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '3px 8px', fontSize: '0.8125rem',
              border: '1px solid var(--color-border)', borderRadius: 4,
              background: 'var(--color-bg)', color: 'var(--color-text)',
              cursor: busy ? 'not-allowed' : 'pointer',
            }}
          ><Plus size={12} /> {addLabel || 'Add row'}</button>
        )}
      </div>

      {paste && !readOnly && (() => {
        const live = paste.rows.filter(k => rows.some(r => rowKeyOf(r) === k && !r._deleted))
        const attnN = live.filter(k => attn.get(k)?.needs).length
        return (
          <div role="status" style={{ background: '#e8f0fe', borderBottom: '1px solid #b9cff5', color: '#0b3f86',
            padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 13 }}>
            <b>{live.length} rows pasted</b>
            <span>into {paste.cols.map(labelOf).map((l, i) => <b key={l}>{i ? ' · ' : ''}{l}</b>)}</span>
            <span style={{ display: 'inline-block', padding: '1px 8px', borderRadius: 9, fontWeight: 600,
              background: STATUS_TONE[1].bg, color: STATUS_TONE[1].fg }}>{live.length - attnN} ready</span>
            {attnN > 0 && (
              <button type="button" onClick={() => setFilter('attention')}
                style={{ padding: '1px 8px', borderRadius: 9, fontWeight: 600, border: 0, cursor: 'pointer',
                  background: ERROR_TONE.bg, color: ERROR_TONE.fg, fontSize: 13 }}>{attnN} need attention</button>
            )}
            <span style={{ flex: 1 }} />
            <button type="button" onClick={() => { onMutate(() => paste.snapshot); rowsRef.current = paste.snapshot; setPaste(null) }}
              style={{ height: 26, padding: '0 10px', borderRadius: 6, border: '1px solid #b9cff5', background: '#fff',
                color: '#0b3f86', cursor: 'pointer', fontSize: 13 }}>Undo paste</button>
            <button type="button" aria-label="Dismiss" onClick={() => setPaste(null)}
              style={{ background: 'none', border: 0, cursor: 'pointer', color: '#0b3f86' }}>✕</button>
          </div>
        )
      })()}
      {showTools && selected.size > 0 && !readOnly && (
        <SelectionBand
          count={selected.size}
          onClear={() => setSelected(new Set())}
          actions={[
            ...(statusColKey ? [{ label: `Set ${labelOf(statusColKey).toLowerCase()} to its limit`, run: setToLimit }] : []),
            { label: 'Delete lines', run: () => { selected.forEach(k => toggleDelete(k)); setSelected(new Set()) }, danger: true },
          ]}
        />
      )}
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
      <div ref={setGridRef} style={{ flex: 1, overflow: 'auto', minWidth: 0 }}
        onFocusCapture={(e) => {
          // The line a Vision line button runs on: the one last clicked / typed in.
          const k = lineListActions.length ? rowOfTarget(e.target) : null
          if (k) setCurrentLineKey(k)
          if (rowCompleteOn) onGridFocus(e)
        }}
        onBlurCapture={rowCompleteOn ? onGridBlur : undefined}>
        {rows.length === 0 && le && !readOnly ? (
          // Line Entry: say how lines can arrive (canvas board 1).
          <LineEntryBar
            mode="empty"
            sm={childSm}
            formNo={formNo}
            execute={(action, payload) => execute(dataAccess, project, objName, action, payload)}
            engineKey={project}
            headerValues={parentHeaderValues || {}}
            parentPk={parentPk}
            txnId={recordTxnId}
            rows={rows}
            onAppend={(added) => onMutate(prev => [...prev, ...added.map(r => ({ ...r, _localId: uuid() }))])}
            notify={txnRt?.notify || { success: () => {}, error: (m) => console.warn(m) }}
            onScan={qaCfg ? () => qaRef.current?.focus() : undefined}
            pasteHint
          />
        ) : rows.length === 0 ? (
          <div style={{
            padding: 20, textAlign: 'center',
            color: 'var(--color-text-secondary)', fontSize: '0.9125rem',
          }}>{readOnly
            ? 'No rows.'
            : `No rows yet — click "${addLabel || 'Add row'}" or paste from Excel.`}</div>
        ) : (
          <table style={{
            borderCollapse: 'collapse', fontSize: '0.9125rem',
            tableLayout: 'fixed', width: tableWidth,
          }}>
            <colgroup>
              {showTools && <col style={{ width: SEL_W }} />}
              <col style={{ width: LINENO_W }} />
              <col style={{ width: STATUS_W }} />
              {cols.map(c => (
                <col
                  key={c.col_id || columnFormKey(c)}
                  data-col-key={colKeyOf(c)}
                  style={{ width: wOf(c) }}
                />
              ))}
              {udaOn && <col style={{ width: DETAILS_W }} />}
              <col style={{ width: ACTIONS_W }} />
            </colgroup>
            <thead>
              <tr>
                {showTools && (
                  <th style={childThStyle}>
                    <input type="checkbox" aria-label="Select all lines shown" checked={allShownSel}
                      disabled={readOnly}
                      onChange={() => setSelected(allShownSel ? new Set()
                        : new Set(shownRows.filter(r => !r._deleted).map(rowKeyOf)))} />
                  </th>
                )}
                <th style={childThStyle}>#</th>
                <th style={childThStyle}></th>
                {cols.map(c => {
                  const ck = colKeyOf(c)
                  const ind = dnd.dropIndicator?.key === ck ? dnd.dropIndicator.side : null
                  return (
                    <th
                      key={c.col_id || columnFormKey(c)}
                      {...dnd.getHeaderProps(ck)}
                      title="Drag to reorder · drag the right edge to resize"
                      style={{
                        ...childThStyle,
                        cursor: 'grab',
                        position: 'relative',
                        width: wOf(c),
                        boxShadow: ind === 'left'
                          ? 'inset 3px 0 0 var(--color-accent)'
                          : ind === 'right'
                            ? 'inset -3px 0 0 var(--color-accent)'
                            : undefined,
                      }}
                    >
                      {listHeadingOf(c)}
                      {isMandatory(c) && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}
                      {lockedCols.has(columnFormKey(c)) && (
                        <Lock size={11} aria-label="Locked on pulled lines" style={{ marginLeft: 4, verticalAlign: 'middle' }} />
                      )}
                      {showTools && !readOnly && !isProtect(c) && (
                        <button type="button" aria-label={`${c.heading || c.name} column menu`}
                          onClick={(e) => { e.stopPropagation(); setColMenu(colMenu === columnFormKey(c) ? null : columnFormKey(c)) }}
                          style={{ background: 'none', border: 0, cursor: 'pointer', padding: 0, marginLeft: 4,
                            color: 'var(--color-text-secondary)', verticalAlign: 'middle' }}>
                          <ChevronDown size={11} />
                        </button>
                      )}
                      {colMenu === columnFormKey(c) && (
                        <div role="menu" onClick={e => e.stopPropagation()} style={{ position: 'absolute', top: '100%', left: 0,
                          zIndex: 20, width: 260, background: 'var(--color-bg)', border: '1px solid var(--color-border)',
                          borderRadius: 8, boxShadow: '0 12px 32px rgba(20,28,40,.18)', padding: '6px 0', textAlign: 'left',
                          fontWeight: 400, cursor: 'default' }}>
                          {[
                            { label: `Set ${String(c.heading || c.name).toLowerCase()} for ${selected.size} selected ${selected.size === 1 ? 'line' : 'lines'}…`,
                              off: selected.size === 0, run: () => setSetFor(c), strong: true },
                            { label: 'Fill down from the first selected line', off: selected.size < 2, run: () => fillDownSelected(c), hint: 'Ctrl D' },
                            { label: 'Clear for selected lines', off: selected.size === 0, run: () => setForRows(c, null, false) },
                          ].map(m => (
                            <button key={m.label} type="button" role="menuitem" disabled={m.off}
                              onClick={() => { setColMenu(null); m.run() }}
                              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', boxSizing: 'border-box',
                                height: 34, padding: '0 12px', border: 0, background: m.strong && !m.off ? '#e8f0fe' : 'transparent',
                                color: m.off ? 'var(--color-text-secondary)' : (m.strong ? '#0b3f86' : 'var(--color-text)'),
                                fontWeight: m.strong ? 600 : 400, fontSize: 13, textAlign: 'left',
                                cursor: m.off ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
                              {m.label}<span style={{ flex: 1 }} />{m.hint && <span style={kbdStyle}>{m.hint}</span>}
                            </button>
                          ))}
                        </div>
                      )}
                      <div {...dnd.getResizeHandleProps(ck)} style={RESIZE_HANDLE_STYLE} />
                    </th>
                  )
                })}
                {udaOn && <th style={childThStyle}>Details</th>}
                <th style={childThStyle}></th>
              </tr>
            </thead>
            <tbody>
              {shownRows.map((row) => {
                const rowKey = rowKeyOf(row)
                const deleted = !!row._deleted
                const errored = !!row._error
                if (!deleted) visibleLine += 1
                const st = rowStatus(row)
                const isRowExpanded = canNest && expandedRowKey === rowKey
                return (
                  <Fragment key={rowKey}>
                  <tr style={{
                    borderBottom: '1px solid var(--color-border)',
                    boxShadow: selected.has(rowKey) ? 'inset 3px 0 0 var(--color-accent)' : undefined,
                    background: errored
                      ? 'rgba(239,68,68,0.10)'
                      : deleted ? 'rgba(239,68,68,0.06)'
                        : currentLineKey === rowKey && lineListActions.length ? 'rgba(76,141,255,0.10)'
                          : undefined,
                    textDecoration: deleted ? 'line-through' : undefined,
                    opacity: deleted ? 0.6 : 1,
                  }}>
                    {showTools && (
                      <td style={{ ...childTdStyle, textAlign: 'center' }}>
                        {!deleted && (
                          <input type="checkbox" aria-label={`Select line ${lineNoOf.get(rowKey) ?? ''}`}
                            checked={selected.has(rowKey)} disabled={readOnly} onChange={() => toggleSel(rowKey)} />
                        )}
                      </td>
                    )}
                    <td style={{ ...childTdStyle, color: 'var(--color-text-secondary)' }}>
                      {canNest && !deleted && (
                        <button
                          type="button"
                          onClick={() => { void toggleExpand(row) }}
                          title={isRowExpanded ? 'Hide nested detail rows' : 'Show nested detail rows'}
                          style={{
                            background: 'transparent', border: 'none', cursor: 'pointer',
                            padding: 0, marginRight: 2, verticalAlign: 'middle',
                            color: 'var(--color-text-secondary)',
                          }}
                        >{isRowExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}</button>
                      )}
                      {deleted ? '—' : (lineNoOf.get(rowKey) ?? visibleLine)}
                    </td>
                    <td style={{ ...childTdStyle, textDecoration: 'none' }}>
                      {st ? (
                        <span
                          onClick={errored ? () => navigateToCell(rowKey, firstErrorCol(row)) : undefined}
                          title={row._error || undefined}
                          style={{
                            display: 'inline-block', padding: '1px 6px', borderRadius: 8,
                            fontSize: '0.8125rem', fontWeight: 700, textTransform: 'uppercase',
                            letterSpacing: '0.03em', background: st.bg, color: st.fg,
                            cursor: errored ? 'pointer' : 'default',
                          }}
                        >{st.label}</span>
                      ) : null}
                      {(() => {
                        // A pulled line's chip follows the user's edits:
                        // the engine resolved the bounds, this only compares.
                        if (!row._limits || deleted) return null
                        const v = evaluateLineLimits(row, row._limits, k => {
                          const cc = cols.find(c => columnFormKey(c) === k)
                          return cc ? String(cc.heading || cc.name) : k
                        })
                        const msg = [...Object.values(v.errors), ...Object.values(v.warnings)].join('\n')
                        if (!v.status && !msg) return null
                        const errs = Object.keys(v.errors).length > 0
                        const tone = errs ? ERROR_TONE : v.status ? STATUS_TONE[v.status.index] : STATUS_TONE[0]
                        const delta = v.status?.delta != null ? ` ${Math.abs(v.status.delta).toLocaleString()}` : ''
                        return (
                          <span title={msg || undefined} style={{
                            display: 'inline-block', marginTop: 2, padding: '1px 6px', borderRadius: 8,
                            fontSize: '0.8125rem', fontWeight: 700, background: tone.bg, color: tone.fg,
                          }}>{v.status?.label ? `${v.status.label}${delta}` : '!'}</span>
                        )
                      })()}
                      {udaLineFlags.length > 0 && !deleted && (() => {
                        const raised = evaluateFlags(udaLineFlags,
                          { ...row, ...driverValues(uda.columns, uda.rowOf(rowKey)?.drivers) },
                          { headerValues: parentHeaderValues })
                        return raised.length
                          ? <span style={{ display: 'inline-flex', marginLeft: 4, verticalAlign: 'middle' }}><FlagIcons flags={raised} size={22} /></span>
                          : null
                      })()}
                    </td>
                    {cols.map(c => {
                      const db = columnFormKey(c)
                      // An additional attribute this line does not have.
                      if (!cellApplies(c, uda.rowOf(rowKey)?.attr_keys)) {
                        return (
                          <td key={c.col_id || db} style={childTdCellStyle}
                            data-grid-cell={`${rowKey}::${db}`} data-grid-row={rowKey} data-grid-col={db}
                            data-tw-na="1">
                            <span style={{ color: 'var(--color-text-faint, var(--color-text-secondary))', fontStyle: 'italic', fontSize: 13, padding: '0 4px' }}>n/a</span>
                          </td>
                        )
                      }
                      return (
                        <td
                          key={c.col_id || db}
                          style={(row._protect?.[db] === 1 || lockedCols.has(db))
                            ? { ...childTdCellStyle, background: LT.rowAlt } : childTdCellStyle}
                          data-grid-cell={`${rowKey}::${db}`}
                          data-grid-row={rowKey}
                          data-grid-col={db}
                        >
                          {/* A pending-delete row AND a view-only record both
                              render the value as plain text instead of an
                              editor. The strike-through / opacity styling is
                              keyed off `deleted` alone, so a locked row does
                              not look deleted. */}
                          {(deleted || readOnly) ? (
                            <span style={{ fontSize: '0.9125rem' }}>
                              {c.uda
                                ? <UdaValueCell col={c} value={row[db]} attrKeys={uda.rowOf(rowKey)?.attr_keys} />
                                : row[db] == null || row[db] === '' ? '' : String(row[db])}
                            </span>
                          ) : c.uda && cellMissing(c, row[db], uda.rowOf(rowKey)?.attr_keys) ? (
                            // A required attribute still empty: the editor,
                            // with the prototype's red "Missing" beside it.
                            <div style={{ position: 'relative' }}>
                              <InlineCellEditor
                                project={project}
                                objName={objName}
                                col={c}
                                value={row[db]}
                                error={cellError(row, c)}
                                readOnlyKey={!!row._txn_id}
                                viewOnly={row._protect?.[db] === 1 || udaLocked(rowKey, db)}
                                onChange={(v) => onCellChange(rowKey, db, v)}
                                rowValues={row}
                                headerValues={parentHeaderValues}
                                uploadDocument={uploadDocument}
                              />
                              <span style={{ position: 'absolute', right: 26, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                                <UdaMissingLabel />
                              </span>
                            </div>
                          ) : (
                            <InlineCellEditor
                              project={project}
                              objName={objName}
                              col={c}
                              value={row[db]}
                              error={cellError(row, c)}
                              readOnlyKey={!!row._txn_id}
                              viewOnly={row._protect?.[db] === 1 || udaLocked(rowKey, db)}
                              onChange={(v) => onCellChange(rowKey, db, v)}
                              rowValues={row}
                              headerValues={parentHeaderValues}
                              uploadDocument={uploadDocument}
                            />
                          )}
                        </td>
                      )
                    })}
                    {udaOn && (
                      <td style={{ ...childTdStyle, textDecoration: 'none' }}>
                        {!deleted && (() => {
                          const res = uda.rowOf(rowKey)
                          if (!res) return null
                          return (
                            <UdaDetailsChip
                              counts={detailsCounts(uda.columns, row, res.attr_keys)}
                              label={`Open details for line ${lineNoOf.get(rowKey) ?? visibleLine}`}
                              onOpen={() => setUdaDrawerKey(rowKey)}
                            />
                          )
                        })()}
                      </td>
                    )}
                    {/* The cell itself always renders (ACTIONS_W and the
                        nested row's colSpan both count it); only its control
                        drops out on a view-only record. */}
                    <td style={{ ...childTdStyle, textAlign: 'center' }}>
                      {!readOnly && (
                        <button
                          type="button"
                          onClick={() => toggleDelete(rowKey)}
                          title={deleted ? 'Restore row' : 'Delete row (pending until Save All)'}
                          style={{
                            background: 'transparent', border: 'none',
                            cursor: 'pointer', padding: '2px 4px',
                            color: deleted ? 'var(--color-accent)' : '#ef4444',
                          }}
                        >{deleted ? <RotateCcw size={13} /> : <Trash2 size={13} />}</button>
                      )}
                    </td>
                  </tr>
                  {isRowExpanded && (
                    <tr>
                      <td colSpan={cols.length + 3 + (showTools ? 1 : 0) + (udaOn ? 1 : 0)} style={{
                        padding: 0,
                        background: 'var(--color-surface-2, var(--color-bg-secondary))',
                        borderBottom: '1px solid var(--color-border)',
                      }}>
                        {/* Pin the nested panel to THIS grid's visible viewport
                            width (sticky/left:0) so the grandchild grid scrolls
                            inside its own bounds and its Add toolbar stays on
                            screen, instead of stretching to this grid's full
                            (wide) table width. Mirrors the top-level FragmentRow. */}
                        <div style={{
                          position: viewportWidth ? 'sticky' : 'static',
                          left: 0,
                          width: viewportWidth || undefined,
                          maxWidth: viewportWidth || undefined,
                          boxSizing: 'border-box',
                          padding: '6px 6px 8px 22px',
                          display: 'flex', flexDirection: 'column', gap: 8,
                        }}>
                          {myChildForms.map(gc => (
                            <div key={gc.formNo} style={{
                              border: '1px solid var(--color-border)', borderRadius: 6,
                              background: 'var(--color-bg)', overflow: 'hidden',
                            }}>
                              <div style={{
                                padding: '3px 8px', fontSize: '0.8125rem', fontWeight: 600,
                                background: 'var(--color-surface-2, var(--color-bg-secondary))',
                                borderBottom: '1px solid var(--color-border)',
                                color: 'var(--color-text-secondary)',
                              }}>{gc.sm.form_title || `Detail ${gc.formNo}`}</div>
                              <div style={{ height: 240, overflow: 'hidden' }}>
                                <ChildTabularGrid
                                  execute={execute}
                                  project={project}
                                  objName={objName}
                                  formNo={gc.formNo}
                                  childSm={gc.sm}
                                  rows={(row.__children?.[gc.formNo] as ChildRow[]) || []}
                                  onMutate={mutateGrandchild(rowKey, gc.formNo)}
                                  parentHeaderValues={row}
                                  uploadDocument={uploadDocument}
                                  dataAccess={dataAccess}
                                  busy={busy}
                                  parentStatus={
                                    row._txn_id ? (row._edited ? 'dirty' : 'saved') : 'unsaved'
                                  }
                                  gridLayout={gridLayout}
                                  model={model}
                                  recordId={recordId}
                                  recordTxnId={row._txn_id != null ? String(row._txn_id) : undefined}
                                  loadChildRows={loadChildRows}
                                  seedRowChildren={seedRowChildren}
                                  depth={depth + 1}
                                  readOnly={readOnly}
                                  onAncestorPatch={patchFromNested(rowKey)}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
      {qaCfg && qa.log.length > 0 && !logHidden && (
        <ScanLog qa={qa} onClose={() => setLogHidden(true)} />
      )}
      </div>
      {showTools && liveCount > 0 && (
        <div style={{ borderTop: '1px solid var(--color-border)', background: 'var(--color-surface-2, var(--color-bg-secondary))',
          padding: '7px 12px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', fontSize: 13 }}>
          <span><b>{liveCount.toLocaleString()}</b> {liveCount === 1 ? 'line' : 'lines'}</span>
          {totalCols.map(c => {
            const k = columnFormKey(c)
            const sum = rows.reduce((a, r) => a + (r._deleted ? 0 : (Number(r[k]) || 0)), 0)
            return <span key={k}><b style={{ fontFamily: LT.mono }}>{sum.toLocaleString()}</b> {String(c.heading || c.name).toLowerCase()}</span>
          })}
          {counts.attention > 0 && (
            <button type="button" onClick={() => setFilter('attention')}
              style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', color: '#9a4a00', fontWeight: 600,
                fontSize: 13, fontFamily: 'inherit' }}>
              {counts.attention} need attention
            </button>
          )}
          {qaCfg && logHidden && qa.log.length > 0 && (
            <button type="button" onClick={() => setLogHidden(false)}
              style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', color: 'var(--color-accent)', fontSize: 13 }}>
              Scan log ({qa.log.length})
            </button>
          )}
          <span style={{ flex: 1 }} />
          {!readOnly && (
            <span style={{ color: 'var(--color-text-secondary)', display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={kbdStyle}>Ctrl D</span> fill down <span style={kbdStyle}>Alt ↓</span> next issue
              {qaCfg && <><span style={kbdStyle}>F6</span> scan</>}
            </span>
          )}
        </div>
      )}
      {udaDrawerKey && (() => {
        const row = rows.find(r => rowKeyOf(r) === udaDrawerKey)
        if (!row) return null
        const res = uda.rowOf(udaDrawerKey)
        const no = lineNoOf.get(udaDrawerKey)
        const first = cols.find(c => !c.uda && !isKey(c) && row[columnFormKey(c)] != null && String(row[columnFormKey(c)]).trim() !== '')
        const label = first ? String(row[columnFormKey(first)]) : ''
        return (
          <UdaRowDrawer
            title={`Line ${no ?? ''}${label ? ` · ${label}` : ''}`}
            subject={label || undefined}
            dynamicProtect={udaLocks[udaDrawerKey]}
            sm={childSm}
            columns={uda.columns}
            resolution={res}
            values={row}
            errors={row._fieldErrors}
            flags={evaluateFlags(udaLineFlags,
              { ...row, ...driverValues(uda.columns, res?.drivers) }, { headerValues: parentHeaderValues })}
            headerValues={parentHeaderValues}
            onChange={(k, v) => onCellChange(udaDrawerKey, k, v)}
            readOnly={readOnly || !!row._deleted}
            project={project}
            objName={objName}
            formNo={formNo}
            notice={uda.error}
            onClose={() => setUdaDrawerKey(null)}
          />
        )
      })()}
      {setFor && (
        <SetForSelectedDialog
          col={setFor}
          count={selected.size}
          blankCount={rows.filter(r => selected.has(rowKeyOf(r)) && !r._deleted
            && (r[columnFormKey(setFor)] == null || r[columnFormKey(setFor)] === '')).length}
          renderInput={(value, onChange) => (
            <InlineCellEditor
              project={project} objName={objName} col={setFor} value={value}
              readOnlyKey={false} onChange={onChange} rowValues={{}} uploadDocument={uploadDocument}
            />
          )}
          onCancel={() => setSetFor(null)}
          onApply={(value, onlyBlank) => { setForRows(setFor, value, onlyBlank); setSetFor(null) }}
        />
      )}
    </div>
  )
}

// Nested-detail grid uses the same Excel-like data surface as the parent grid:
// gridlines on all sides, a header that blends with the app theme, and cells
// that adapt to the theme (white-on-dark-text in light, light-on-dark in dark).
const childThStyle: React.CSSProperties = {
  textAlign: 'left', padding: '6px 8px', fontWeight: 600,
  fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
  background: 'var(--color-surface-2, var(--color-bg-secondary))',
  border: '1px solid var(--color-border)',
  position: 'sticky', top: 0, zIndex: 1,
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
}
const childTdStyle: React.CSSProperties = {
  padding: '4px 8px', color: 'var(--color-text)',
  background: 'var(--lp-input-bg, var(--color-bg))',
  border: '1px solid var(--color-border)',
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  verticalAlign: 'middle',
}
const childTdCellStyle: React.CSSProperties = {
  padding: '3px 5px', color: 'var(--color-text)',
  background: 'var(--lp-input-bg, var(--color-bg))',
  border: '1px solid var(--color-border)',
  verticalAlign: 'middle',
}
